import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { FaCheckCircle, FaMinus, FaPlus, FaTimesCircle } from "react-icons/fa";
import {
  buildDynamicChecklistExport,
  emptyDynamicChecklistExportContext,
  exportDocumentHasContent,
  extractDynamicSnapshot,
  fetchDynamicChecklistExportContext,
  fetchDynamicChecklistMaster,
  parseChecklistPayload,
  DynamicChecklistExportDocument,
  ExportFieldRow,
} from "../../utils/dynamicChecklistExport";

const getBgColor = (variant?: string) => {
  if (variant === "warning")
    return { bg: "#fff3cd", text: "#856404", border: "#ffeeba" };
  return { bg: "#007bff", text: "#fff", border: "#007bff" };
};

const VerifyBadge: React.FC<{ verified: boolean | null }> = ({ verified }) => {
  if (verified === null) return null;
  return (
    <span style={{ marginLeft: 8, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {verified ? (
        <>
          <FaCheckCircle color="#28a745" /> Verified
        </>
      ) : (
        <>
          <FaTimesCircle color="#dc3545" /> Not verified
        </>
      )}
    </span>
  );
};

const FieldRow: React.FC<{ row: ExportFieldRow }> = ({ row }) => (
  <div
    style={{
      display: "flex",
      flexWrap: "wrap",
      padding: "8px 0",
      borderBottom: "1px solid #eee",
      paddingLeft: (row.indent ?? 0) * 16,
    }}
  >
    <div style={{ flex: "0 0 38%", fontWeight: 600, color: "#333" }}>
      {row.label}
    </div>
    <div style={{ flex: "1 1 50%", color: "#444" }}>
      {row.displayValue}
      <VerifyBadge verified={row.verified} />
    </div>
  </div>
);

const DynamicChecklistPreview: React.FC<{ data: any }> = ({ data }) => {
  const parsed = useMemo(
    () => parseChecklistPayload(data?.Current_json),
    [data?.Current_json]
  );
  const dynamic = useMemo(() => extractDynamicSnapshot(parsed), [parsed]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportDoc, setExportDoc] = useState<DynamicChecklistExportDocument | null>(null);
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    if (!dynamic) {
      setLoading(false);
      setError("No dynamic checklist data in this record.");
      return;
    }
    setLoading(true);
    setError(null);
    const fileId = data?.file_id;
    Promise.all([
      fetchDynamicChecklistMaster(),
      fileId ? fetchDynamicChecklistExportContext(fileId) : emptyDynamicChecklistExportContext(),
    ])
      .then(([master, exportContext]) => {
        if (cancelled) return;
        if (!master.length) {
          setError("Dynamic checklist master is not configured.");
          setExportDoc(null);
          return;
        }
        const doc = buildDynamicChecklistExport(master, dynamic, parsed?.remarks, exportContext);
        if (!exportDocumentHasContent(doc)) {
          setError("No meaningful checklist data to preview.");
          setExportDoc(null);
          return;
        }
        setExportDoc(doc);
        setOpenSections(new Set(doc.sections.map((s) => s.id)));
      })
      .catch(() => {
        if (!cancelled) setError("Could not load dynamic checklist master.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dynamic, parsed?.remarks, data?.file_id]);

  const toggleSection = (id: string) => {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="p-4 text-muted text-center">Loading dynamic checklist preview…</div>
    );
  }

  if (error || !exportDoc) {
    return (
      <div className="p-4 text-danger text-center">{error || "Nothing to preview."}</div>
    );
  }

  return (
    <div
      style={{
        padding: "10px",
        background: "#f8f9fa",
        height: "100%",
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div
        style={{
          textAlign: "center",
          marginBottom: 24,
          padding: "20px",
          background: "#fff",
          borderRadius: 8,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        }}
      >
        <h4 style={{ marginBottom: 8 }}>{data.title} - Checklist Preview</h4>
        <p style={{ margin: 0, color: "#666", fontSize: 14 }}>
          {data.file_id} • Login at{" "}
          {moment(data.login_dt).format("DD MMMM YYYY, hh:mm A")} • {data.bank_name}
        </p>
        <p style={{ margin: "8px 0 0", color: "#666", fontSize: 14 }}>
          Completed at {moment(data.date).format("DD MMMM YYYY, hh:mm A")} •{" "}
          {data.officerType} • Updated by {data.downloadedBy}
        </p>
      </div>

      {exportDoc.sections.map((section) => {
        const colors = getBgColor(section.headerVariant);
        const open = openSections.has(section.id);
        return (
          <div key={section.id} style={{ marginBottom: 20 }}>
            <div
              onClick={() => toggleSection(section.id)}
              style={{
                padding: "14px 20px",
                backgroundColor: colors.bg,
                color: colors.text,
                fontWeight: 600,
                fontSize: 16,
                borderRadius: open ? "8px 8px 0 0" : 8,
                cursor: "pointer",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                border: `1px solid ${colors.border}`,
              }}
            >
              <span>{section.title}</span>
              {open ? <FaMinus /> : <FaPlus />}
            </div>
            {open && (
              <div
                style={{
                  background: "#fff",
                  padding: "12px 20px",
                  border: `1px solid ${colors.border}`,
                  borderTop: "none",
                  borderRadius: "0 0 8px 8px",
                }}
              >
                {section.blocks.map((block, bi) => (
                  <div key={bi} style={{ marginBottom: block.title ? 16 : 0 }}>
                    {block.title && (
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: 14,
                          marginBottom: 8,
                          color: "#555",
                          background: "#f1f3f5",
                          padding: "6px 10px",
                          borderRadius: 4,
                        }}
                      >
                        {block.title}
                      </div>
                    )}
                    {block.rows.map((row) => (
                      <FieldRow key={row.key} row={row} />
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {exportDoc.remarks && (
        <div
          style={{
            marginTop: 30,
            padding: 20,
            backgroundColor: "#f8d7da",
            borderRadius: 8,
            border: "1px solid #f5c6cb",
          }}
        >
          <strong style={{ color: "#721c24" }}>Remarks:</strong>
          <p style={{ margin: "10px 0 0", color: "#721c24", fontSize: 15 }}>
            {exportDoc.remarks}
          </p>
        </div>
      )}
    </div>
  );
};

export default DynamicChecklistPreview;
